// @givmo/fundraisers-module — the layer screens' pure helpers (FCM-WEB25): what the settings read says is
// live, how a campaign's end is dated and named (FCM-BE61: in the time zone its deadline was set in, so
// every viewer reads the same moment), and how a refused layer act is told.
import {
  firstErrorCode,
  isGivmoError,
  type P2pConfigOut,
  type P2pConfigRequest,
} from '@givmo/api-client';
import { fieldFromDate, zonedInstant } from './emails-decode';
import { LAYERS_COPY } from './layers-copy';

/** Each flagged group of settings keys is present only while its backend switch is on (dark == missing). */
export function layersLive(config: P2pConfigOut | null | undefined): boolean {
  return config != null && config.default_card_style !== undefined;
}
export function endsLive(config: P2pConfigOut | null | undefined): boolean {
  return config != null && config.deadline_at !== undefined;
}
export function recipientChoiceLive(config: P2pConfigOut | null | undefined): boolean {
  return config != null && config.recipient_choice !== undefined;
}
/** FCM-BE100's name rides the layers' switch, keyed on its own key: a backend before it takes none. */
export function layerNameLive(config: P2pConfigOut | null | undefined): boolean {
  return config != null && config.layer_name !== undefined;
}

/**
 * FCM-BE35: whether a campaign above this one has ended it. The cascade stamps open rows only, so a draft
 * under an ended campaign still reads Draft, while the server refuses its publish and every change under
 * it. Its settings say so: an end that has passed and is not its own deadline's (the campaign whose end
 * it is, or, when that one's name is hidden from the reader, a campaign above whose end differs from its
 * own deadline).
 */
export function endedFromAbove(
  config: P2pConfigOut | null | undefined,
  hasParent: boolean,
  now: Date = new Date(),
): boolean {
  if (!endsLive(config) || !config?.ends_at) return false;
  const end = Date.parse(config.ends_at);
  if (!Number.isFinite(end) || end > now.getTime()) return false;
  if (config.ends_with != null) return true;
  return hasParent && (config.deadline_at == null || Date.parse(config.deadline_at) !== end);
}

/** The reader's own IANA time zone: a new deadline's when the organization has none (FCM-BE61). */
export function readerTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * The zone a new deadline is set in: the organization's own (chosen in Settings, else derived from
 * its state, off the host's standing read), else the reader's. A New York charity's deadline ends at
 * 11:59 PM in New York, whoever sets it (the KCC dev build, 2026-10-31).
 */
export function deadlineZone(orgTimeZone: string | null | undefined): string {
  return knownZone(orgTimeZone) ?? readerTimeZone();
}

function knownZone(zone: string | null | undefined): string | undefined {
  if (!zone) return undefined;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return zone;
  } catch {
    return undefined;
  }
}

/** "Pacific Time": a time zone by its everyday name. With none, the reader's own. */
export function zoneName(zone: string | null | undefined, at: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: knownZone(zone),
    timeZoneName: 'longGeneric',
  }).formatToParts(at);
  return parts.find((part) => part.type === 'timeZoneName')?.value ?? zone ?? '';
}

/** "Dec 31, 2027, at 11:59 PM PST": an end, in the time zone it was set in (else the reader's). */
export function formatEnd(iso: string, zone?: string | null): string {
  const date = new Date(iso);
  const timeZone = knownZone(zone);
  const day = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeZone }).format(date);
  const time = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
    timeZone,
  }).format(date);
  return `${day}, at ${time}`;
}

/** "Aug 31, 2026": the day an end falls on, where it was set. */
export function formatEndDay(iso: string, zone?: string | null): string {
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeZone: knownZone(zone),
  }).format(new Date(iso));
}

/**
 * The calendar day a deadline falls on in the zone it was set in, as the date picker holds a day (local
 * midnight), so a deadline set in another zone opens on its own day.
 */
export function deadlineDay(iso: string, zone?: string | null): Date {
  const parts = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    timeZone: knownZone(zone),
  }).formatToParts(new Date(iso));
  const read = (type: 'year' | 'month' | 'day') =>
    Number(parts.find((part) => part.type === type)?.value ?? '0');
  return new Date(read('year'), read('month') - 1, read('day'));
}

/**
 * A day chosen in the picker, ending at 11:59 PM there (the ruled end): in `zone` when one is named,
 * else in the reader's own time zone.
 */
export function endOfDay(day: Date, zone?: string): string {
  if (zone !== undefined) {
    const instant = zonedInstant({ date: fieldFromDate(day), time: '23:59' }, zone);
    if (instant !== null) return instant;
  }
  const end = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 23, 59, 0, 0);
  return end.toISOString();
}

/**
 * The settings write a chosen day makes (FCM-BE61): its 11:59 PM in `zone` (the organization's, else
 * the reader's: `deadlineZone`), named with it;
 * no day clears the deadline, and names no zone, which the server takes only with a deadline.
 */
export function deadlineBody(
  day: Date | undefined,
  zone: string = readerTimeZone(),
): Pick<P2pConfigRequest, 'deadline_at' | 'deadline_time_zone'> {
  return day
    ? { deadline_at: endOfDay(day, zone), deadline_time_zone: zone }
    : { deadline_at: null };
}

/** The same calendar day, whichever times of it two dates carry. */
export function sameDay(a: Date | undefined, b: Date | undefined): boolean {
  if (a === undefined || b === undefined) return a === b;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Whole days left until an end, rounded up; null once it has passed. */
export function daysLeft(iso: string | null | undefined, now: Date = new Date()): number | null {
  if (!iso) return null;
  const left = new Date(iso).getTime() - now.getTime();
  if (!Number.isFinite(left) || left <= 0) return null;
  return Math.ceil(left / 86_400_000);
}

/** FCM-BE100's `LAYER_NAME_MAX_LENGTH`: the most characters either form of a name may hold. */
export const LAYER_NAME_MAX_LENGTH = 40;

/** A name form as the server keeps it (`collapse_layer_name`): each run of whitespace one space, the
 * ends trimmed. */
export function collapseLayerName(value: string): string {
  return value.split(/\s+/).filter(Boolean).join(' ');
}

/** What a campaign stored for the campaigns inside it (FCM-BE100's `layer_name`), or none: a name
 * missing either form is none, as the donate pages and the apps read it. */
export function storedLayerName(
  config: P2pConfigOut | null | undefined,
): { singular: string; plural: string } | null {
  const name = config?.layer_name;
  return name != null && typeof name.singular === 'string' && typeof name.plural === 'string'
    ? { singular: name.singular, plural: name.plural }
    : null;
}

/**
 * The settings write two typed forms make (FCM-BE100): both, collapsed as the server collapses them, and
 * both empty clears the name. One form without the other goes as typed, so its refusal is the server's
 * own sentence (D-FCM-52's decision 4).
 */
export function layerNameBody(
  singular: string,
  plural: string,
): Pick<P2pConfigRequest, 'layer_name'> {
  const one = collapseLayerName(singular);
  const many = collapseLayerName(plural);
  return { layer_name: one || many ? { singular: one, plural: many } : null };
}

const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/** The campaign a pasted public link names: the id in it. */
export function campaignIdFromLink(text: string): string | null {
  const match = UUID.exec(text.trim());
  return match ? match[0].toLowerCase() : null;
}

/**
 * Refusals that leave another choice open (decision 6): a different campaign to add, move under or invite
 * may still go. Any other 409 means the structure moved under the page, so the dialog closes and the page
 * re-reads (FCM-WEB28's ruling 4).
 */
const ANOTHER_CHOICE_MAY_WORK = new Set([
  'CAMPAIGN_LAYER_ATTACHED',
  'CAMPAIGN_LAYER_CYCLE',
  'CAMPAIGN_LAYER_TOO_DEEP',
  'CAMPAIGN_LAYER_TREE_FULL',
  'CAMPAIGN_LAYER_RECIPIENTS',
  'CAMPAIGN_LAYER_STATE',
  'CAMPAIGN_LAYER_INVITE_ORG_ONLY',
]);

/** A refused layer act, in the server's own words. */
export function layerRefusal(error: unknown): { message: string; stale: boolean } {
  if (!isGivmoError(error)) return { message: LAYERS_COPY.layerFailed, stale: false };
  const code = firstErrorCode(error) ?? '';
  return {
    message: error.message || LAYERS_COPY.layerFailed,
    stale: error.status === 409 && !ANOTHER_CHOICE_MAY_WORK.has(code),
  };
}
